import {
  Badge,
  Box,
  Flex,
  Grid,
  Heading,
  Spinner,
  Text,
} from "@chakra-ui/react"
import { useQuery } from "@tanstack/react-query"
import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { type ReactNode, useMemo } from "react"
import { useTranslation } from "react-i18next"
import { FiActivity, FiArrowLeft, FiInfo } from "react-icons/fi"

import { OpenAPI, TagTemplatesService } from "@/client"
import type { TagStateEnum, TagTemplateRead, TagTypeEnum } from "@/client"
import { IconBox, VisionButton, VisionCard } from "@/components/VisionUI"
import { useOrganization } from "@/contexts/OrganizationContext"
import { useSemanticColors } from "@/hooks/useSemanticColors"
import { getTagTypeMeta } from "@/lib/tagTypeMeta"

export const Route = createFileRoute("/_layout/tag-states")({
  component: TagStatesPage,
})

// Lifecycle states, in the backend declaration order
// (backend/app/models/tag.py::TagStateEnum). States are a fixed enum —
// not editable from the dashboard.
const TAG_STATE_ORDER: TagStateEnum[] = [
  "NEW",
  "SLEEP",
  "STANDBY",
  "READY",
  "ARMED",
  "BAP_MODE",
  "LOGGING",
  "FINISHED",
  "INVALID",
  "ALERT",
]

// Swatch colour per state — mirrors getStateColor() on the /tags list so
// the same state reads the same colour across the app.
const STATE_COLOR: Record<TagStateEnum, string> = {
  ALERT: "red",
  INVALID: "red",
  LOGGING: "green",
  ARMED: "blue",
  READY: "teal",
  NEW: "orange",
  FINISHED: "purple",
  SLEEP: "gray",
  STANDBY: "gray",
  BAP_MODE: "gray",
}

interface PaginatedTagRow {
  id: string
  name: string
  epcid: string
  sku?: string | null
  category?: string | null
  state?: TagStateEnum | null
  tag_type?: TagTypeEnum | null
  tag_template_id?: string | null
}

function authedFetch(path: string, init: RequestInit = {}) {
  const token = localStorage.getItem("access_token")
  const base = (OpenAPI.BASE || "").replace(/\/$/, "")
  return fetch(`${base}${path}`, {
    ...init,
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
      ...(init.headers || {}),
    },
  })
}

function TagStatesPage() {
  const sc = useSemanticColors()
  const { t } = useTranslation(["admin", "tags"])
  const navigate = useNavigate()
  const { currentOrganization } = useOrganization()
  const orgId = currentOrganization?.id

  // Templates — used only to resolve tag_template_id → a readable name
  // for the "templates in this state" chain.
  const { data: templates = [], isLoading: templatesLoading } = useQuery({
    queryKey: ["tag-templates", orgId],
    queryFn: async (): Promise<TagTemplateRead[]> => {
      if (!orgId) return []
      try {
        return await TagTemplatesService.readOrganizationTemplates({
          organisationId: orgId,
          skip: 0,
          limit: 1000,
        })
      } catch {
        return []
      }
    },
    enabled: !!orgId,
    staleTime: 30_000,
  })

  // State has no FK to type/category/template/SKU — those associations
  // only exist THROUGH individual tags (a tag carries a state plus a
  // type, category, sku, and template). So we fetch tags and aggregate.
  const { data: tags = [], isLoading: tagsLoading } = useQuery({
    queryKey: ["tag-states-tags", orgId],
    queryFn: async (): Promise<PaginatedTagRow[]> => {
      if (!orgId) return []
      const r = await authedFetch(
        `/api/v1/organizations/${orgId}/tags/paginated?limit=1000`,
      )
      if (!r.ok) throw new Error(await r.text())
      const body = (await r.json()) as { data: PaginatedTagRow[] }
      return body.data ?? []
    },
    enabled: !!orgId,
    staleTime: 30_000,
  })

  const templateNameById = useMemo(() => {
    const m = new Map<string, string>()
    for (const tpl of templates) m.set(tpl.id, tpl.name)
    return m
  }, [templates])

  const uniqSorted = (values: (string | null | undefined)[]) =>
    Array.from(
      new Set(values.map((v) => v?.trim()).filter((v): v is string => !!v)),
    ).sort((a, b) => a.localeCompare(b))

  const grouped = useMemo(() => {
    return TAG_STATE_ORDER.map((state) => {
      const stateTags = tags.filter((tag) => tag.state === state)
      const tagTypes = uniqSorted(stateTags.map((tg) => tg.tag_type ?? null))
      const categories = uniqSorted(stateTags.map((tg) => tg.category))
      const skus = uniqSorted(stateTags.map((tg) => tg.sku))
      const templateNames = uniqSorted(
        stateTags.map((tg) =>
          tg.tag_template_id
            ? (templateNameById.get(tg.tag_template_id) ?? null)
            : null,
        ),
      )
      return {
        state,
        tagCount: stateTags.length,
        tagTypes,
        categories,
        skus,
        templateNames,
      }
    })
  }, [tags, templateNameById])

  const isLoading = templatesLoading || tagsLoading

  if (!orgId) {
    return (
      <Box>
        <Text color={sc.textMuted}>
          {t("common:org.selectPrompt", "Select an organisation to continue.")}
        </Text>
      </Box>
    )
  }

  return (
    <Box>
      <Flex mb={4} align="center" gap={3}>
        <VisionButton
          variant="ghost"
          size="sm"
          onClick={() => navigate({ to: "/admin-tags" })}
        >
          <FiArrowLeft />
          <Text ml={2}>{t("tagStates.back", "Back to Tags")}</Text>
        </VisionButton>
      </Flex>

      <Flex align="center" gap={3} mb={4} flexWrap="wrap">
        <IconBox variant="glass" size="lg">
          <FiActivity fontSize="24px" color={sc.iconColor} />
        </IconBox>
        <Box flex="1">
          <Heading size="lg" color={sc.heading}>
            {t("tagStates.title", "Tag States")}
          </Heading>
          <Text color={sc.textMuted}>
            {t(
              "tagStates.subtitle",
              "The fixed lifecycle states a tag can be in, and what's currently in each.",
            )}
          </Text>
        </Box>
      </Flex>

      {/* Read-only notice — states are a backend enum, not editable. */}
      <VisionCard mb={6}>
        <Flex gap={3} p={4} align="flex-start">
          <Box mt="2px">
            <FiInfo fontSize="18px" color={sc.iconColor} />
          </Box>
          <Box>
            <Text color={sc.heading} fontWeight="600" fontSize="sm" mb={1}>
              {t("tagStates.readOnlyTitle", "Tag states are read-only")}
            </Text>
            <Text color={sc.textMuted} fontSize="sm">
              {t(
                "tagStates.readOnlyBody",
                "Tag states are defined in the backend code (TagStateEnum in backend/app/models/tag.py) and are driven by the tag lifecycle. They cannot be added, renamed, or removed from the dashboard — changing the set of states requires a feature request to the development team.",
              )}
            </Text>
            <Text color={sc.textMuted} fontSize="xs" mt={2} fontStyle="italic">
              {t(
                "tagStates.derivedNote",
                "A state has no direct link to tag types, categories, templates, or SKUs. The associations below are derived from the tags currently in each state.",
              )}
            </Text>
          </Box>
        </Flex>
      </VisionCard>

      {isLoading ? (
        <Flex justify="center" py={10}>
          <Spinner size="xl" color={sc.spinnerColor} />
        </Flex>
      ) : (
        <Grid templateColumns={{ base: "1fr", lg: "repeat(2, 1fr)" }} gap={4}>
          {grouped.map(
            ({
              state,
              tagCount,
              tagTypes,
              categories,
              skus,
              templateNames,
            }) => (
              <VisionCard key={state}>
                <Box p={4}>
                  {/* Header: state label + raw enum value + tag count */}
                  <Flex align="center" gap={3} mb={4} flexWrap="wrap">
                    <Box flex="1" minW={0}>
                      <Heading size="md" color={sc.heading}>
                        {t(`tags:states.${state}`, state)}
                      </Heading>
                      <Text
                        color={sc.textMuted}
                        fontSize="xs"
                        fontFamily="mono"
                      >
                        {state}
                      </Text>
                    </Box>
                    <Badge colorPalette={STATE_COLOR[state]} size="sm">
                      {t("tagStates.tagCount", "{{count}} tags", {
                        count: tagCount,
                      })}
                    </Badge>
                  </Flex>

                  {/* Tag types currently in this state */}
                  <ChainSection
                    label={t("tagStates.section.types", "Tag types")}
                    count={tagTypes.length}
                    sc={sc}
                  >
                    {tagTypes.length === 0 ? (
                      <EmptyHint sc={sc} text={t("tagStates.none", "None.")} />
                    ) : (
                      <Flex gap={1} wrap="wrap">
                        {tagTypes.map((ty) => {
                          const meta = getTagTypeMeta(ty as TagTypeEnum)
                          return (
                            <Badge key={ty} colorPalette={meta.color} size="sm">
                              {meta.icon} {t(meta.labelKey, ty)}
                            </Badge>
                          )
                        })}
                      </Flex>
                    )}
                  </ChainSection>

                  {/* Templates of tags in this state */}
                  <ChainSection
                    label={t("tagStates.section.templates", "Templates")}
                    count={templateNames.length}
                    sc={sc}
                  >
                    {templateNames.length === 0 ? (
                      <EmptyHint sc={sc} text={t("tagStates.none", "None.")} />
                    ) : (
                      <Flex gap={1} wrap="wrap">
                        {templateNames.map((n) => (
                          <Badge key={n} colorPalette="gray" size="sm">
                            {n}
                          </Badge>
                        ))}
                      </Flex>
                    )}
                  </ChainSection>

                  {/* Categories of tags in this state */}
                  <ChainSection
                    label={t("tagStates.section.categories", "Categories")}
                    count={categories.length}
                    sc={sc}
                  >
                    {categories.length === 0 ? (
                      <EmptyHint sc={sc} text={t("tagStates.none", "None.")} />
                    ) : (
                      <Flex gap={1} wrap="wrap">
                        {categories.map((c) => (
                          <Badge key={c} colorPalette="purple" size="sm">
                            {c}
                          </Badge>
                        ))}
                      </Flex>
                    )}
                  </ChainSection>

                  {/* SKU bindings of tags in this state */}
                  <ChainSection
                    label={t("tagStates.section.skus", "SKU bindings")}
                    count={skus.length}
                    sc={sc}
                    last
                  >
                    {skus.length === 0 ? (
                      <EmptyHint sc={sc} text={t("tagStates.none", "None.")} />
                    ) : (
                      <Flex gap={1} wrap="wrap">
                        {skus.map((s) => (
                          <Badge
                            key={s}
                            colorPalette="teal"
                            size="sm"
                            fontFamily="mono"
                          >
                            {s}
                          </Badge>
                        ))}
                      </Flex>
                    )}
                  </ChainSection>
                </Box>
              </VisionCard>
            ),
          )}
        </Grid>
      )}
    </Box>
  )
}

function ChainSection({
  label,
  count,
  sc,
  last,
  children,
}: {
  label: string
  count: number
  sc: ReturnType<typeof useSemanticColors>
  last?: boolean
  children: ReactNode
}) {
  return (
    <Box
      pb={last ? 0 : 3}
      mb={last ? 0 : 3}
      borderBottom={last ? undefined : "1px solid"}
      borderColor="rgba(255,255,255,0.08)"
    >
      <Text
        fontSize="xs"
        color={sc.textMuted}
        textTransform="uppercase"
        letterSpacing="wider"
        mb={2}
      >
        {label} ({count})
      </Text>
      {children}
    </Box>
  )
}

function EmptyHint({
  sc,
  text,
}: {
  sc: ReturnType<typeof useSemanticColors>
  text: string
}) {
  return (
    <Text
      fontSize="sm"
      color={sc.textSubtle ?? sc.textMuted}
      fontStyle="italic"
    >
      {text}
    </Text>
  )
}
